import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const API_BASE = "/api/ebay-intelligence";
const OUTCOME_STATUSES = [
  { value: "open", label: "Open" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "actioned", label: "Actioned" },
  { value: "deferred", label: "Deferred" },
  { value: "needs_follow_up", label: "Needs follow-up" },
  { value: "ignored", label: "Ignored" },
];
const EMPTY_DRAFT = {
  recommendation_source: "action_queue",
  source_recommendation_id: "",
  listing_id: "",
  sku: "",
  recommendation_key: "",
  recommendation_title: "",
  recommendation_summary: "",
  outcome_status: "open",
  outcome_reason: "",
  business_feedback: "",
  operator_name: "",
  follow_up_at: "",
};

const numberText = (value) => Number(value || 0).toLocaleString();
const dateText = (value) => {
  if (!value) return "-";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};
const statusLabel = (value) => OUTCOME_STATUSES.find((item) => item.value === value)?.label || value || "Open";
const statusColor = (value) => {
  if (["accepted", "actioned"].includes(value)) return "success";
  if (["rejected", "ignored"].includes(value)) return "error";
  if (["deferred", "needs_follow_up"].includes(value)) return "warning";
  return "default";
};

function SummaryCard({ label, value, helper }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{numberText(value)}</Typography>
      {helper && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{helper}</Typography>}
    </Paper>
  );
}

export default function RecommendationOutcomeTracker({ onOpenActionQueue }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [includeResolved, setIncludeResolved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schemaReady, setSchemaReady] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);

  const loadOutcomes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "500" });
      if (statusFilter !== "ALL") params.set("status", statusFilter);
      if (includeResolved) params.set("include_resolved", "true");
      const res = await fetch(`${API_BASE}/recommendation-outcomes?${params.toString()}`);
      const json = await res.json();
      if (!res.ok || json.ok === false) throw new Error(json.error || json.message || "Failed to load recommendation outcomes");
      setSchemaReady(json.schema_ready !== false);
      setRows(Array.isArray(json.rows) ? json.rows : []);
      setSummary(json.summary || {});
      if (json.schema_ready === false) setMessage(json.message || "Recommendation outcome schema is not installed yet.");
    } catch (e) {
      setError(e.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [includeResolved, statusFilter]);

  useEffect(() => {
    loadOutcomes();
  }, [loadOutcomes]);

  const openDraft = (row) => {
    setDraft(row ? { ...EMPTY_DRAFT, ...row, listing_id: row.listing_id || "", follow_up_at: row.follow_up_at || "" } : EMPTY_DRAFT);
    setDialogOpen(true);
  };

  const saveOutcome = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/recommendation-outcomes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) throw new Error(json.error || json.message || "Failed to save recommendation outcome");
      setDialogOpen(false);
      setMessage(json.message || "Recommendation outcome saved.");
      await loadOutcomes();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    { field: "recommendation_title", headerName: "Recommendation", flex: 1.2, minWidth: 220 },
    { field: "recommendation_source", headerName: "Source", flex: 0.7, minWidth: 130 },
    { field: "sku", headerName: "SKU", flex: 0.7, minWidth: 120 },
    {
      field: "outcome_status",
      headerName: "Outcome",
      flex: 0.7,
      minWidth: 140,
      renderCell: (params) => <Chip size="small" color={statusColor(params.value)} label={statusLabel(params.value)} />,
    },
    { field: "outcome_reason", headerName: "Reason", flex: 0.9, minWidth: 160 },
    { field: "business_feedback", headerName: "Business feedback", flex: 1.2, minWidth: 240 },
    { field: "updated_at", headerName: "Updated", flex: 0.8, minWidth: 160, valueFormatter: (value) => dateText(value) },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => <Button size="small" onClick={() => openDraft(params.row)}>Edit</Button>,
    },
  ], []);

  return (
    <Stack spacing={2}>
      {(loading || saving) && <LinearProgress />}
      {!schemaReady && <Alert severity="warning">Manual SQL install is required before recommendation outcomes can be tracked.</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="Open" value={summary.open} helper="Awaiting decision" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="Accepted" value={summary.accepted} helper="Business-approved" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="Rejected" value={summary.rejected} helper="Business declined" /></Grid>
        <Grid item xs={12} sm={6} md={3}><SummaryCard label="Follow-up due" value={summary.follow_up_due} helper="Unresolved reminders" /></Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="recommendation-outcome-status-label">Outcome</InputLabel>
              <Select labelId="recommendation-outcome-status-label" value={statusFilter} label="Outcome" onChange={(event) => setStatusFilter(event.target.value)}>
                <MenuItem value="ALL">All open</MenuItem>
                {OUTCOME_STATUSES.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={includeResolved} onChange={(event) => setIncludeResolved(event.target.checked)} />} label="Include resolved" />
          </Stack>
          <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} sx={{ flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={loadOutcomes} disabled={loading || saving}>Refresh</Button>
            <Button variant="outlined" onClick={onOpenActionQueue}>Open Action Queue</Button>
            <Button variant="contained" onClick={() => openDraft(null)} disabled={!schemaReady || loading || saving}>Add Outcome</Button>
          </Stack>
        </Stack>
        <Box sx={{ height: 430 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.id || `${row.recommendation_source}-${row.source_recommendation_id}`}
            disableRowSelectionOnClick
            density="compact"
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            localeText={{ noRowsLabel: schemaReady ? "No recommendation outcomes match this filter." : "Install the recommendation outcome SQL to enable tracking." }}
          />
        </Box>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Recommendation outcome</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Source" value={draft.recommendation_source} onChange={(e) => setDraft({ ...draft, recommendation_source: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Source recommendation ID" value={draft.source_recommendation_id} onChange={(e) => setDraft({ ...draft, source_recommendation_id: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Listing ID" value={draft.listing_id} onChange={(e) => setDraft({ ...draft, listing_id: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="SKU" value={draft.sku} onChange={(e) => setDraft({ ...draft, sku: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Recommendation title" value={draft.recommendation_title} onChange={(e) => setDraft({ ...draft, recommendation_title: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Recommendation summary" value={draft.recommendation_summary} onChange={(e) => setDraft({ ...draft, recommendation_summary: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="outcome-status-label">Outcome</InputLabel>
                <Select labelId="outcome-status-label" value={draft.outcome_status} label="Outcome" onChange={(e) => setDraft({ ...draft, outcome_status: e.target.value })}>
                  {OUTCOME_STATUSES.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Reason" value={draft.outcome_reason} onChange={(e) => setDraft({ ...draft, outcome_reason: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Business feedback" value={draft.business_feedback} onChange={(e) => setDraft({ ...draft, business_feedback: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Operator" value={draft.operator_name} onChange={(e) => setDraft({ ...draft, operator_name: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="datetime-local" label="Follow-up" InputLabelProps={{ shrink: true }} value={draft.follow_up_at || ""} onChange={(e) => setDraft({ ...draft, follow_up_at: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveOutcome} disabled={saving || !draft.recommendation_source || !draft.source_recommendation_id}>Save Outcome</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
